import './Quote.css';

import { WorkflowVersion } from '@foxden/version-controller-client';
import React, { useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { Redirect } from 'react-router-dom';
import { z } from 'zod';

import { PaymentClient } from '../../backend-client/paymentBackend';
import { RatingQuotingClient } from '../../backend-client/ratingQuotingBackend';
import Loading from '../../components/Loading/Loading';
import {
  AdditionalInsured as AdditionalInsuredInfo,
  AmtrustEligibleQuoteResponse,
  AmtrustIneligibleQuoteResponse,
  AmtrustResetCoverageRecommendationInput,
  CoveragesData,
  MunichIneligibleQuoteResponse,
  MunichResetCoverageRecommendationInput,
  UpdateCoverageInput,
  UpdateCoverageOutput
} from '../../generated/graphql';
import { UserPaymentPlanOptionByQuoteId } from '../../generated/payment-graphql';
import {
  GetQuoteExpired,
  MunichEligibleQuoteResponse,
  QuoteInfo
} from '../../generated/rating-quoting-graphql';
import { useFlags } from '../../utils/useFlags';
import ErrorPage from '../Error/Error';
import AdditionalInsured from './components/AdditionalInsured';
import BuyNowButton from './components/BuyNowButton';
import Coverages from './components/Coverages';
import FeinInputBox from './components/FeinInputBox';
import Premium from './components/Premium';
import { ReturnToQuotePopup } from './components/ReturnToQuotePopup/ReturnToQuotePopup';
import Summary from './components/Summary';
import { translateStringToLimit } from './translateStringToLimit';

export interface CoveragesDataInput {
  name: string;
  chooseCoverage: boolean;
  finalPremium: number;
  limits: Record<string, number>;
}

interface QuoteProps {
  applicationId: string;
  quoteInfo?: QuoteInfo;
  munichQuote?: MunichEligibleQuoteResponse | MunichIneligibleQuoteResponse;
  amtrustQuote?: AmtrustIneligibleQuoteResponse | AmtrustEligibleQuoteResponse;
  returnToQuoteExpired?: GetQuoteExpired;
  versionData?: WorkflowVersion;
}

const additionalInsuedInfoSchema = z.object({
  name: z.string().min(1),
  address: z.object({
    street: z.string().min(1),
    postalCode: z.string().min(5),
    province: z.string().min(2),
    city: z.string().min(1),
    address2: z.string().nullable().optional()
  }),
  email: z
    .string()
    .refine((value) => value === '')
    .or(z.string().email())
});

// 2 cases for this component
// GetQuoteOk: quoteInfo will be defined
// GetQuoteExpired: returnToQuoteExpired will be defined

const Quote: React.FC<QuoteProps> = (props) => {
  const flags = useFlags();
  const { amTrustFullFlow } = flags;

  const {
    amtrustQuote,
    applicationId,
    munichQuote,
    returnToQuoteExpired,
    quoteInfo,
    versionData
  } = props;

  const closePopup = () => {
    window.location.reload();
  };

  const [munichQuoteResponse, setMunichQuoteResponse] = useState(munichQuote);
  const [amtrustQuoteResponse, setAmtrustQuoteResponse] = useState(() => {
    return amtrustQuote?.__typename === 'AmtrustEligibleQuoteResponse'
      ? amtrustQuote
      : undefined;
  });
  const [country, setCountry] = useState('');
  const [province, setProvince] = useState('');

  const [premium, setPremium] = useState(() => {
    const munichPremium =
      munichQuoteResponse?.__typename === 'MunichEligibleQuoteResponse'
        ? munichQuoteResponse?.premium ?? 0
        : 0;
    const amtrustPremium = getAmTrustPremium(amtrustQuoteResponse);

    return flags.amTrustFullFlow
      ? munichPremium + amtrustPremium
      : munichPremium;
  });

  const [additionalInsuredInfoList, setAdditionalInsuredInfoList] = useState<
    AdditionalInsuredInfo[] | undefined
  >(undefined);
  const [
    fillAllAdditionalInsuredInfo,
    setFillAllAdditionalInsuredInfo
  ] = useState<boolean | undefined>(undefined);
  let additionalInsuredCoverage: CoveragesData | undefined = undefined;
  if (munichQuoteResponse?.__typename === 'MunichEligibleQuoteResponse') {
    munichQuoteResponse?.coveragesData.forEach((coverage) => {
      if (coverage.name === 'additionalInsured') {
        additionalInsuredCoverage = coverage;
        return;
      }
    });
  }
  const enableAdditionalInsured =
    munichQuoteResponse?.__typename === 'MunichEligibleQuoteResponse' &&
    additionalInsuredCoverage;

  const isEndorsement = quoteInfo?.transactionType === 'Endorsement';

  const [
    updateCoverageInput,
    setUpdateCoverageInput
  ] = useState<UpdateCoverageInput>();
  const [
    updateCoverageData,
    setUpdateCoverageData
  ] = useState<UpdateCoverageOutput>();
  const [
    isUpdateCoverageByRatingQuotingLoading,
    setIsUpdateCoverageByRatingQuotingLoading
  ] = useState<boolean>(false);
  const [updateCoverageError, setUpdateCoverageError] = useState();

  const [
    resetRecommendationData,
    setResetRecommendationData
  ] = useState<UpdateCoverageOutput>();
  const [
    isResetCoverageRecommendationByRatingQuotingLoading,
    setIsResetCoverageRecommendationByRatingQuotingLoading
  ] = useState<boolean>(false);
  const [resetRecommendationError, setResetRecommendationError] = useState();

  const [quoteId, setQuoteId] = useState<string | undefined>(undefined);

  const [paymentOptionData, setPaymentOptionData] = useState<
    Array<UserPaymentPlanOptionByQuoteId>
  >();
  const [
    isGetUserPaymentPlanOptionByQuoteIdLoading,
    setIsGetUserPaymentPlanOptionByQuoteIdLoading
  ] = useState<boolean>(false);
  const [
    getUserPaymentPlanOptionByQuoteIdError,
    setGetUserPaymentPlanOptionByQuoteIdError
  ] = useState();

  useEffect(() => window.scrollTo(0, 0), []);
  useEffect(() => {
    if (munichQuoteResponse) {
      // assign quoteId to state
      const id =
        munichQuoteResponse.__typename === 'MunichEligibleQuoteResponse'
          ? munichQuoteResponse.quoteId
          : undefined;
      setQuoteId(id);

      const { region } = munichQuoteResponse;
      setCountry(region == 'Canada' ? 'Canada' : 'United States of America');
      // for now, consider "Canada" as province for data record, other US states will use individual state name
      setProvince(region);

      const tagManagerArgs = {
        dataLayer: {
          event: 'form-progress',
          type: 'commercial',
          country: country,
          'state/province': province,
          step: 'Munich_Quote'
        }
      };
      TagManager.dataLayer(tagManagerArgs);
    }
  }, [munichQuoteResponse, country, province]);

  useEffect(() => {
    const defaultAdditionalInsuredInfoList = quoteInfo?.additionalInsuredInfoList || [
      { name: '', address: undefined, email: '' }
    ];
    // only set the AdditionalInsured default value for valid cases
    if (enableAdditionalInsured && additionalInsuredCoverage?.chooseCoverage) {
      setAdditionalInsuredInfoList(defaultAdditionalInsuredInfoList);
    }
  }, [additionalInsuredCoverage, enableAdditionalInsured, quoteInfo]);

  useEffect(() => {
    if (resetRecommendationData) {
      const { munichOutput, amtrustOutput } = resetRecommendationData;
      if (munichOutput) {
        if (!munichQuoteResponse) {
          throw new Error('getQuote has no munich response');
        }
        if (munichQuoteResponse?.__typename === 'MunichEligibleQuoteResponse') {
          setMunichQuoteResponse({
            ...munichQuoteResponse,
            coveragesData: munichOutput.coveragesData,
            quoteId: munichOutput.quoteId,
            premium: munichOutput.finalPremium
          });
        }
      }

      if (amtrustOutput) {
        if (!amtrustQuoteResponse) {
          throw new Error('getQuote has no amtrust response');
        }

        const { premium, wcLimit } = amtrustOutput;

        if (!wcLimit) {
          throw new Error('wcLimit should be defined');
        }

        const limit = translateStringToLimit(wcLimit);

        if (
          amtrustQuoteResponse?.__typename === 'AmtrustEligibleQuoteResponse'
        ) {
          setAmtrustQuoteResponse({
            ...amtrustQuoteResponse,
            premium,
            limit
          });
        }
      }
      const amtrustPremium = amTrustFullFlow ? amtrustOutput?.premium ?? 0 : 0;
      setPremium(amtrustPremium + (munichOutput?.finalPremium ?? 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetRecommendationData]);

  useEffect(() => {
    if (updateCoverageInput && updateCoverageData) {
      const { amtrustOutput, munichOutput } = updateCoverageData;
      const amtrustPremium = (() => {
        if (updateCoverageInput.amtrustInput) {
          if (!amtrustQuoteResponse) {
            throw new Error('amtrust getQuote has no response');
          }
          if (!amtrustOutput) {
            throw new Error('updateCoverage has no amtrust response');
          }
          if (amtrustOutput.__typename !== 'AmtrustUpdateCoverageOutput') {
            throw new Error(
              'amtrust updateCoverage response is not AmtrustUpdateCoverageOutput'
            );
          }
          const { feinIsValid } = amtrustOutput;
          if (!amtrustOutput) {
            throw new Error('amtrust updateCoverage has no response');
          }
          setAmtrustQuoteResponse({
            ...amtrustQuoteResponse,
            ...(feinIsValid != null ? { feinIsValid } : {}), // only update FEIN/SSN if it's not null/undefined
            premium: amtrustOutput.premium,
            limit: updateCoverageInput.amtrustInput.wcLimit
          });
          return amtrustOutput.premium;
        }
        return getAmTrustPremium(amtrustQuoteResponse);
      })();

      const munichPremium = (() => {
        if (updateCoverageInput.munichInput) {
          if (!munichQuoteResponse) {
            throw new Error('getQuote has no munich Response');
          }
          if (!munichOutput) {
            throw new Error('updateCoverage has no munich response');
          }
          if (
            munichQuoteResponse?.__typename === 'MunichEligibleQuoteResponse'
          ) {
            setMunichQuoteResponse({
              ...munichQuoteResponse,
              coveragesData: munichOutput.coveragesData,
              quoteId: munichOutput.quoteId,
              premium: munichOutput.finalPremium
            });
            return munichOutput.finalPremium;
          }
        }
        return munichQuoteResponse?.__typename === 'MunichEligibleQuoteResponse'
          ? munichQuoteResponse?.premium ?? 0
          : 0;
      })();

      flags.amTrustFullFlow
        ? setPremium(amtrustPremium + munichPremium)
        : setPremium(munichPremium);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateCoverageData]);

  useEffect(() => {
    if (!isEndorsement && quoteId !== undefined) {
      setIsGetUserPaymentPlanOptionByQuoteIdLoading(true);

      const paymentClient = new PaymentClient(
        versionData?.paymentBackendVersion
      );

      paymentClient
        .getUserPaymentPlanOptionByQuoteId(quoteId)
        .then((data) => {
          setPaymentOptionData(data);
          setIsGetUserPaymentPlanOptionByQuoteIdLoading(false);
        })
        .catch((err) => {
          setGetUserPaymentPlanOptionByQuoteIdError(err);
          setIsGetUserPaymentPlanOptionByQuoteIdLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, versionData, isEndorsement]);

  const onUpdateCoverage = async (input: UpdateCoverageInput) => {
    setUpdateCoverageInput(input);

    // call backend to update coverage and get a new premium
    if (versionData) {
      setIsUpdateCoverageByRatingQuotingLoading(true);

      const ratingQuotingClient = new RatingQuotingClient(
        versionData.quoteBackendVersion
      );
      ratingQuotingClient
        .updateCoverage(input)
        .then((data) => {
          setUpdateCoverageData(data);
          setIsUpdateCoverageByRatingQuotingLoading(false);
        })
        .catch((err) => {
          setUpdateCoverageError(err);
          setIsUpdateCoverageByRatingQuotingLoading(false);
        });
    }
  };

  const onResetRecommendation = async () => {
    const munichInput: MunichResetCoverageRecommendationInput | undefined =
      munichQuoteResponse?.__typename === 'MunichEligibleQuoteResponse'
        ? {
            quoteId: munichQuoteResponse.quoteId,
            applicationId,
            region: munichQuoteResponse.region
          }
        : undefined;
    const amtrustInput:
      | AmtrustResetCoverageRecommendationInput
      | undefined = amtrustQuoteResponse
      ? { quoteId: amtrustQuoteResponse.amtrustquoteId }
      : undefined;

    if (versionData) {
      setIsResetCoverageRecommendationByRatingQuotingLoading(true);
      const ratingQuotingClient = new RatingQuotingClient(
        versionData.quoteBackendVersion
      );
      ratingQuotingClient
        .resetCoverageRecommendation({ amtrustInput, munichInput })
        .then((data) => {
          setResetRecommendationData(data);
          setIsResetCoverageRecommendationByRatingQuotingLoading(false);
        })
        .catch((err) => {
          setResetRecommendationError(err);
          setIsResetCoverageRecommendationByRatingQuotingLoading(false);
        });
    }
  };

  if (resetRecommendationError || updateCoverageError) {
    return <ErrorPage />;
  }

  if (
    isUpdateCoverageByRatingQuotingLoading ||
    isResetCoverageRecommendationByRatingQuotingLoading ||
    isGetUserPaymentPlanOptionByQuoteIdLoading ||
    (!quoteId && !returnToQuoteExpired)
  ) {
    return <Loading />;
  }

  if (!returnToQuoteExpired && getUserPaymentPlanOptionByQuoteIdError) {
    throw Error('Backend error');
  }

  const totalPayable = paymentOptionData
    ? paymentOptionData[0].totalPayable
    : premium;

  const showSendQuoteButton =
    munichQuoteResponse?.__typename === 'MunichEligibleQuoteResponse'
      ? munichQuoteResponse?.showSendQuoteButton
      : false;

  const validateAdditionalInsured = () => {
    let validataionResult = true;
    if (
      additionalInsuredCoverage?.['chooseCoverage'] &&
      additionalInsuredInfoList
    ) {
      additionalInsuredInfoList.forEach((info) => {
        try {
          additionalInsuedInfoSchema.parse(info);
        } catch (e) {
          setFillAllAdditionalInsuredInfo(false);
          validataionResult = false;
          return;
        }
      });
    }
    return validataionResult;
  };

  if (premium <= 0 && !isEndorsement && !returnToQuoteExpired) {
    return (
      <Redirect
        to={{
          pathname: '/complete',
          state: {
            country,
            province
          }
        }}
      />
    );
  }

  return (
    <div className="relative page-container font-raleway lg:my-4">
      {!returnToQuoteExpired ? (
        <div className="mt-1 quote-box">
          <div className="quote-header md:hidden text-center mb-2">
            <p className="text-2xl text-navy-dark font-bold">
              Tailored Quote For:
            </p>
            <p className="text-xl font-medium">{quoteInfo?.companyName}</p>
          </div>
          <Premium
            yearlyPremium={premium}
            isEndorsement={isEndorsement}
            paymentOptionData={paymentOptionData}
          />
          <Summary
            amtrustSummaryInput={amtrustQuoteResponse}
            munichSummaryInput={munichQuoteResponse}
            yearlyPremium={premium}
            totalPayable={totalPayable}
            isEndorsement={isEndorsement}
            carrierPartner={quoteInfo?.carrierPartner}
          />
          <Coverages
            munichCoverageInput={munichQuoteResponse}
            amtrustCoverageInput={amtrustQuote}
            onUpdateCoverage={onUpdateCoverage}
            onResetRecommendation={onResetRecommendation}
            isEndorsement={isEndorsement}
            applicationId={applicationId}
            carrierPartner={quoteInfo?.carrierPartner}
          />
          {amTrustFullFlow && amtrustQuoteResponse?.feinIsValid === false ? (
            <FeinInputBox
              amtrustQuoteInput={amtrustQuoteResponse}
              onUpdateCoverage={onUpdateCoverage}
            />
          ) : null}
          {munichQuoteResponse?.__typename === 'MunichEligibleQuoteResponse' &&
          additionalInsuredCoverage ? (
            <AdditionalInsured
              coverageData={additionalInsuredCoverage}
              onUpdateCoverage={onUpdateCoverage}
              region={munichQuoteResponse.region}
              quoteId={munichQuoteResponse.quoteId}
              applicationId={applicationId}
              additionalInsuredInfoList={additionalInsuredInfoList}
              setAdditionalInsuredInfoList={setAdditionalInsuredInfoList}
              fillAllAdditionalInsuredInfo={fillAllAdditionalInsuredInfo}
              setFillAllAdditionalInsuredInfo={setFillAllAdditionalInsuredInfo}
              disabled={munichQuoteResponse.disableAdditionalInsuredCheckbox}
            />
          ) : null}
          <BuyNowButton
            munichQuoteId={quoteId}
            amtrustQuoteId={Number(quoteId)}
            applicationId={applicationId}
            showSendQuoteButton={showSendQuoteButton}
            country={country}
            quoteInfo={quoteInfo}
            disabled={amTrustFullFlow && !amtrustQuoteResponse?.feinIsValid}
            setQuoteId={setQuoteId}
            additionalInsuredInfoList={additionalInsuredInfoList}
            validateAdditionalInsured={validateAdditionalInsured}
          />
        </div>
      ) : returnToQuoteExpired &&
        returnToQuoteExpired.isQuoteExpired &&
        returnToQuoteExpired.quoteExpireDate ? (
        <ReturnToQuotePopup
          isQuoteExpired={true}
          applicationId={applicationId}
          returnToQuoteExpired={returnToQuoteExpired}
          closeAction={closePopup}
        />
      ) : returnToQuoteExpired &&
        returnToQuoteExpired.isEffectiveDateExpired &&
        returnToQuoteExpired.quoteExpireDate ? (
        <ReturnToQuotePopup
          isQuoteExpired={false}
          applicationId={applicationId}
          returnToQuoteExpired={returnToQuoteExpired}
          closeAction={closePopup}
        />
      ) : null}
    </div>
  );
};

export default Quote;

function getAmTrustPremium(
  quote?: AmtrustIneligibleQuoteResponse | AmtrustEligibleQuoteResponse
): number {
  if (quote?.__typename === 'AmtrustEligibleQuoteResponse') {
    return quote.premium ?? 0;
  }

  return 0;
}
